// qlearn/frontend/src/lib/offline/audioSizes.ts
/**
 * Reads the per-reciter size manifests generated by
 * scripts/generate-audio-sizes.mjs (served from /audio-sizes/<id>.json and
 * runtime-cached by the SW). Returns null when no manifest is available so
 * callers can degrade to "size unknown" instead of blocking the download.
 */

interface SizesFile {
  v: number
  totalBytes: number
  surahs: Record<string, number>
}

const cache = new Map<string, SizesFile | null>()
const inFlight = new Map<string, Promise<SizesFile | null>>()

async function loadSizes(reciter: string): Promise<SizesFile | null> {
  if (cache.has(reciter)) return cache.get(reciter) ?? null
  const pending = inFlight.get(reciter)
  if (pending) return pending
  const promise = (async (): Promise<SizesFile | null> => {
    try {
      const res = await fetch(`/audio-sizes/${reciter}.json`)
      if (!res.ok) {
        // Confirmed: the manifest doesn't exist (or the server errored) at
        // this URL. That won't change until a redeploy, so cache it.
        cache.set(reciter, null)
        return null
      }
      const data = (await res.json()) as SizesFile
      if (data.v !== 1 || typeof data.surahs !== 'object') {
        // Confirmed: the response is malformed. Same reasoning — cache it.
        cache.set(reciter, null)
        return null
      }
      cache.set(reciter, data)
      return data
    } catch {
      // Transient network-level failure (fetch rejected, body couldn't be
      // read, etc.) rather than a confirmed absence — do NOT cache, so a
      // later call retries instead of permanently pinning "no estimate"
      // for the rest of the session in this offline-first app.
      return null
    } finally {
      inFlight.delete(reciter)
    }
  })()
  inFlight.set(reciter, promise)
  return promise
}

/**
 * Estimated audio bytes for the given surahs (deduplicated) with this
 * reciter. 0 for an empty list; null when no estimate is available.
 */
export async function getSurahsSizeEstimate(
  reciter: string,
  surahNumbers: number[],
): Promise<number | null> {
  const unique = Array.from(new Set(surahNumbers))
  if (unique.length === 0) return 0
  const sizes = await loadSizes(reciter)
  if (!sizes) return null
  let sum = 0
  for (const n of unique) {
    const bytes = sizes.surahs[String(n)]
    if (typeof bytes !== 'number') return null
    sum += bytes
  }
  return sum
}

export function resetAudioSizesCacheForTests(): void {
  cache.clear()
  inFlight.clear()
}
